import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import { Search } from 'lucide-react'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { Input } from '@/components/ui/input'
import { Skeleton } from '@/components/ui/skeleton'
import { useAllInstructors } from '@/hooks/useAllInstructors'
import { formatPrecise } from '@/lib/format'
import { workoutNouns } from '@/lib/discipline'

// This list mixes instructors across every discipline (many teach more
// than one), so "workouts" rather than a discipline-specific noun.
const { plural: WORKOUTS } = workoutNouns('all')

function initials(name: string) {
  return name
    .split(' ')
    .map((part) => part[0])
    .slice(0, 2)
    .join('')
    .toUpperCase()
}

export function InstructorsListPage() {
  const { data, error } = useAllInstructors()
  const [query, setQuery] = useState('')

  const filtered = useMemo(() => {
    if (!data) return null
    const q = query.trim().toLowerCase()
    return q ? data.filter((i) => i.name.toLowerCase().includes(q)) : data
  }, [data, query])

  return (
    <div className="mx-auto flex max-w-6xl flex-col gap-6 px-6 py-10">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">All instructors</h1>
        <p className="text-sm text-muted-foreground">
          {data ? `${formatPrecise(data.length)} instructors tracked` : 'Loading...'}
        </p>
      </div>

      <div className="relative max-w-sm">
        <Search className="absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
        <Input
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search instructors..."
          className="pl-9"
        />
      </div>

      {error && <p className="text-sm text-destructive">Couldn&apos;t load instructors: {error}</p>}

      {filtered ? (
        <ul className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {filtered.map((instructor) => (
            <li key={instructor.id}>
              <Link
                to={`/instructors/${instructor.id}`}
                className="flex items-center gap-3 rounded-xl border border-border bg-card p-4 hover:bg-muted"
              >
                <Avatar className="size-10 shrink-0 ring-1 ring-border">
                  <AvatarImage src={instructor.image_url ?? undefined} alt="" />
                  <AvatarFallback className="text-xs">{initials(instructor.name)}</AvatarFallback>
                </Avatar>
                <div className="flex min-w-0 flex-col">
                  <span className="truncate text-sm font-medium">{instructor.name}</span>
                  <span className="text-xs text-muted-foreground">
                    {formatPrecise(instructor.ride_count)} {WORKOUTS}
                  </span>
                </div>
              </Link>
            </li>
          ))}
          {filtered.length === 0 && (
            <li className="col-span-full text-sm text-muted-foreground">No instructors match &ldquo;{query}&rdquo;</li>
          )}
        </ul>
      ) : (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
          {Array.from({ length: 12 }).map((_, i) => (
            <Skeleton key={i} className="h-[68px] rounded-xl" />
          ))}
        </div>
      )}
    </div>
  )
}
